<template>
  <v-row>
    <v-col cols="12">
      <v-card class="rpt-card" flat>
        <!-- HEADER BAND -->
        <div class="rpt-header pl-base-bg">
          <div class="rpt-header-icon">
            <v-icon color="#fff" size="22">mdi-chart-line</v-icon>
          </div>
          <div>
            <div class="rpt-title">P&amp;L Report</div>
            <div class="rpt-subtitle">Analyze income, expenses and transactions</div>
          </div>
        </div>

        <div class="pa-6">
          <!-- FILTERS -->
          <div class="rpt-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.type"
                  :items="reportTypes"
                  item-text="text"
                  item-value="value"
                  label="Report Type"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <!-- MONTH -->
              <v-col cols="12" md="3" v-if="filters.type === 'month'">
                <v-menu
                  v-model="monthMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                  min-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedMonth"
                      label="Month"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <v-date-picker
                    v-model="filters.month"
                    type="month"
                    @input="monthMenu = false"
                  />
                </v-menu>
              </v-col>

              <!-- YEAR -->
              <v-col cols="12" md="3" v-if="filters.type === 'year'">
                <v-text-field
                  v-model="filters.year"
                  type="number"
                  label="Year"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <!-- RANGE -->
              <template v-if="filters.type === 'range'">
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="fromMenu"
                    :close-on-content-click="false"
                    offset-y
                    max-width="290"
                    min-width="290"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedFrom"
                        label="From"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="filters.from"
                      @change="handleFromChange"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12" md="3">
                  <v-menu
                    v-model="toMenu"
                    :close-on-content-click="false"
                    offset-y
                    max-width="290"
                    min-width="290"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedTo"
                        label="To"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker v-model="filters.to" @change="handleToChange" />
                  </v-menu>
                </v-col>
              </template>

              <!-- APPLY + PDF + EXCEL -->
              <v-col cols="12" md="3" class="rpt-actions">
                <v-btn class="rpt-apply-btn" @click="loadReports" :loading="loading">
                  Apply
                </v-btn>

                <v-btn
                  outlined
                  color="#d93025"
                  class="ml-2"
                  :disabled="transactions.length === 0"
                  @click="downloadPDF"
                >
                  <v-icon left size="16" color="#d93025">mdi-file-pdf-box</v-icon>
                  PDF
                </v-btn>

                <v-btn
                  outlined
                  color="#1d6f42"
                  class="ml-2"
                  :disabled="transactions.length === 0"
                  @click="downloadExcel"
                >
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- KPI -->
          <v-row dense class="mb-6">
            <v-col cols="12" md="4">
              <v-card class="rpt-kpi-card rpt-kpi-card--income pa-4" flat>
                <div class="rpt-kpi-label">Income</div>
                <div class="rpt-kpi-value">{{ formatINR(summary.income) }}</div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card class="rpt-kpi-card rpt-kpi-card--expense pa-4" flat>
                <div class="rpt-kpi-label">Expense</div>
                <div class="rpt-kpi-value">{{ formatINR(summary.expense) }}</div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card
                class="rpt-kpi-card pa-4"
                :class="summary.net >= 0 ? 'rpt-kpi-card--profit' : 'rpt-kpi-card--expense'"
                flat
              >
                <div class="rpt-kpi-label">Net</div>
                <div class="rpt-kpi-value">{{ formatINR(summary.net) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- GRID -->
          <app-ag-grid
            title="Transactions Report"
            :columnDefs="txnColumns"
            :rowData="transactions"
            :loading="loading"
            :newBtn="false"
            :refreshBtn="false"
            :mobileCard="mobileCard"
            height="height: calc(60vh - 75px)"
          />
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export default {
  name: "ReportsPage",
  components: { AppAgGrid },

  data() {
    return {
      loading: false,

      monthMenu: false,
      fromMenu: false,
      toMenu: false,

      filters: {
        type: "month",
        month: "",
        year: new Date().getFullYear(),
        from: "",
        to: "",
      },

      reportTypes: [
        { text: "Month", value: "month" },
        { text: "Year", value: "year" },
        { text: "Range", value: "range" },
      ],

      txnColumns: [
        {
          headerName: "Date",
          field: "date",
          valueFormatter: (p) => {
            if (!p.value) return "-";
            const d = new Date(p.value);
            return `${String(d.getDate()).padStart(2, "0")}-${String(
              d.getMonth() + 1,
            ).padStart(2, "0")}-${d.getFullYear()}`;
          },
        },
        { headerName: "Customer / Vendor", field: "customerName" },
        { headerName: "Type", field: "type" },
        { headerName: "Category", field: "category" },
        {
          headerName: "Amount",
          field: "amount",
          valueFormatter: (p) => {
            if (p.value == null) return "0";
            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
            }).format(p.value);
          },
        },
        { headerName: "Note", field: "note" },
        { headerName: "User", field: "userId.name" },
        { headerName: "User Email", field: "userId.email" },
      ],
    };
  },

  computed: {
    mobileCard() {
      return {
        title: (row) => row.customerName || row.category || "-",
        subtitle: (row) => row.category || "-",
        badge: (row) => ({
          text: row.type || "-",
          style:
            row.type === "INCOME"
              ? { background: "#e6f7ec", color: "#15803d" }
              : { background: "#fdecea", color: "#c62828" },
        }),
        fields: (row) => [
          { label: "Amount", value: this.formatINR(row.amount) },
        ],
      };
    },

    formattedMonth() {
      if (!this.filters.month) return "";
      const d = new Date(this.filters.month);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    formattedFrom() {
      if (!this.filters.from) return "";
      const [y, m, d] = this.filters.from.split("-");
      return `${d}-${m}-${y}`;
    },

    formattedTo() {
      if (!this.filters.to) return "";
      const [y, m, d] = this.filters.to.split("-");
      return `${d}-${m}-${y}`;
    },

    summary() {
      return (
        this.$store.state.reports.summary || {
          income: 0,
          expense: 0,
          net: 0,
        }
      );
    },

    transactions() {
      return this.$store.state.reports.transactions || [];
    },
  },

  watch: {
    fromMenu(val) {
      if (val) this.toMenu = false;
    },
    toMenu(val) {
      if (val) this.fromMenu = false;
    },
  },

  created() {
    this.setDefaults();
    this.loadReports();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    handleFromChange(val) {
      this.filters.from = val;
      this.fromMenu = false;

      if (this.filters.to && this.filters.to < val) {
        this.filters.to = val;
      }
    },

    handleToChange(val) {
      this.filters.to = val;
      this.toMenu = false;

      if (this.filters.from && val < this.filters.from) {
        this.filters.from = val;
      }
    },

    setDefaults() {
      const today = new Date();

      this.filters.month = today.toISOString().slice(0, 7);

      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      this.filters.from = firstDay.toISOString().substr(0, 10);
      this.filters.to = today.toISOString().substr(0, 10);
    },

    buildParams() {
      const params = {};

      if (this.filters.type === "month") params.month = this.filters.month;
      if (this.filters.type === "year") params.year = this.filters.year;
      if (this.filters.type === "range") {
        params.from = this.filters.from;
        params.to = this.filters.to;
      }

      return params;
    },

    async loadReports() {
      this.loading = true;
      try {
        const params = this.buildParams();
        await this.$store.dispatch("reports/getReport", params);
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    downloadPDF() {
      const doc = new jsPDF();

      doc.setFont("helvetica", "normal");
      doc.setCharSpace(0);

      const pageWidth =
        doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
      const M = 14; // left/right margin, matches autoTable's default

      // ===== BRAND GRADIENT HEADER (matches --pl-base-bg-gradient) =====
      const stops = [
        { t: 0, c: [5, 47, 94] },
        { t: 0.35, c: [10, 90, 144] },
        { t: 0.68, c: [13, 132, 120] },
        { t: 1, c: [21, 163, 85] },
      ];

      const colorAt = (t) => {
        let a = stops[0];
        let b = stops[stops.length - 1];
        for (let i = 0; i < stops.length - 1; i++) {
          if (t >= stops[i].t && t <= stops[i + 1].t) {
            a = stops[i];
            b = stops[i + 1];
            break;
          }
        }
        const span = b.t - a.t || 1;
        const local = (t - a.t) / span;
        return [0, 1, 2].map((i) =>
          Math.round(a.c[i] + (b.c[i] - a.c[i]) * local),
        );
      };

      const headerHeight = 32;
      const steps = 60;

      for (let i = 0; i < steps; i++) {
        const t = i / (steps - 1);
        const [r, g, b] = colorAt(t);
        doc.setFillColor(r, g, b);
        doc.rect(
          (pageWidth / steps) * i,
          0,
          pageWidth / steps + 1,
          headerHeight,
          "F",
        );
      }

      // ===== TITLE + FILTER LINE (both inside the header band) =====
      doc.setFontSize(17);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("Transactions Report", M, 15);

      const filterText =
        this.filters.type === "range"
          ? `From: ${this.formattedFrom}  To: ${this.formattedTo}`
          : `${this.filters.type === "month" ? "Month" : "Year"}: ${this.periodSuffix()}`;

      doc.setFontSize(9.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(225, 240, 255);
      doc.text(filterText, M, 23);

      const generatedOn = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      doc.text(`Generated ${generatedOn}`, pageWidth - M, 15, { align: "right" });

      // ===== FORMAT CURRENCY =====
      const formatPDFCurrency = (val) => {
        if (val == null) return "0.00";

        return new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val);
      };

      // ===== SUMMARY STAT CARDS =====
      const cardY = headerHeight + 10;
      const cardH = 22;
      const gap = 6;
      const cardW = (pageWidth - M * 2 - gap * 2) / 3;

      const cards = [
        { label: "INCOME", value: formatPDFCurrency(this.summary.income), color: [21, 128, 61] },
        { label: "EXPENSE", value: formatPDFCurrency(this.summary.expense), color: [198, 40, 40] },
        {
          label: "NET",
          value: formatPDFCurrency(this.summary.net),
          color: this.summary.net >= 0 ? [10, 90, 144] : [198, 40, 40],
        },
      ];

      cards.forEach((card, i) => {
        const x = M + i * (cardW + gap);

        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(228, 232, 238);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, cardY, cardW, cardH, 2, 2, "FD");

        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(120, 130, 145);
        doc.text(card.label, x + 5, cardY + 8);

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...card.color);
        doc.text(`Rs. ${card.value}`, x + 5, cardY + 17);
      });

      // ===== TABLE DATA =====
      const tableData = this.transactions.map((t) => {
        const d = new Date(t.date);

        return [
          `${String(d.getDate()).padStart(2, "0")}-${String(
            d.getMonth() + 1,
          ).padStart(2, "0")}-${d.getFullYear()}`,
          t.customerName || "-",
          t.type,
          t.category || "-",
          formatPDFCurrency(t.amount),
          t.note || "-",
        ];
      });

      // ===== TABLE =====
      autoTable(doc, {
        startY: cardY + cardH + 10,
        head: [["Date", "Customer / Vendor", "Type", "Category", "Amount", "Note"]],
        body: tableData,

        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
          lineColor: [232, 236, 241],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [10, 90, 144],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: {
          4: { halign: "right", fontStyle: "bold" },
        },

        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 2) {
            data.cell.styles.textColor =
              data.cell.raw === "INCOME" ? [21, 128, 61] : [198, 40, 40];
            data.cell.styles.fontStyle = "bold";
          }
        },

        didDrawPage: function () {
          const pageHeight =
            doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
          const pw =
            doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

          doc.setDrawColor(232, 236, 241);
          doc.setLineWidth(0.3);
          doc.line(M, pageHeight - 16, pw - M, pageHeight - 16);

          doc.setFontSize(8.5);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(148, 163, 184);

          doc.text("ProfitLedgex", pw - M, pageHeight - 10, { align: "right" });
        },
      });

      // ===== PAGE NUMBERS (total page count is only known once every
      // page has been drawn, so this has to be a pass after the table
      // finishes rather than inside didDrawPage) =====
      const pageCount = doc.internal.getNumberOfPages();
      const footerHeight =
        doc.internal.pageSize.height || doc.internal.pageSize.getHeight();

      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(148, 163, 184);
        doc.text(`Page ${p} of ${pageCount}`, M, footerHeight - 10);
      }

      // ===== DOWNLOAD =====
      doc.save("transactions-report.pdf");
    },

    downloadExcel() {
      // ===== FORMAT DATE (for display cells; date/number cells below use
      // real Date/Number values so Excel can sort/sum/filter them) =====
      const formatDate = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1,
        ).padStart(2, "0")}-${d.getFullYear()}`;
      };

      const periodText =
        this.filters.type === "range"
          ? `From: ${this.formattedFrom}  To: ${this.formattedTo}`
          : `${this.filters.type === "month" ? "Month" : "Year"}: ${this.periodSuffix()}`;

      const COLS = ["Date", "Customer / Vendor", "Type", "Category", "Amount", "Note"];

      // ===== TITLE / PERIOD BLOCK =====
      const rows = [
        ["Transactions Report"],
        [periodText],
        [],
        COLS,
      ];
      const headerRowIndex = rows.length - 1; // for freeze/autofilter below

      // ===== TRANSACTION ROWS (Amount as a real number, not a string) =====
      this.transactions.forEach((t) => {
        rows.push([
          formatDate(t.date),
          t.customerName || "-",
          t.type,
          t.category || "-",
          Number(t.amount) || 0,
          t.note || "-",
        ]);
      });

      // ===== SUMMARY BLOCK (clearly separated, own labeled rows — not
      // jammed into the transaction table's columns) =====
      rows.push(
        [],
        ["Summary"],
        ["Income", "", "", "", Number(this.summary.income) || 0],
        ["Expense", "", "", "", Number(this.summary.expense) || 0],
        ["Net", "", "", "", Number(this.summary.net) || 0],
      );

      // ===== CREATE SHEET =====
      const worksheet = XLSX.utils.aoa_to_sheet(rows);

      // ===== COLUMN WIDTHS (all 6 columns, Note included) =====
      worksheet["!cols"] = [
        { wch: 12 }, // Date
        { wch: 18 }, // Customer
        { wch: 10 }, // Type
        { wch: 18 }, // Category
        { wch: 14 }, // Amount
        { wch: 30 }, // Note
      ];

      // ===== NUMBER FORMAT ON AMOUNT CELLS (2 decimals, matches the
      // app's currency display without hardcoding it as text) =====
      const amountColIndex = 4;
      for (let r = headerRowIndex + 1; r < rows.length; r++) {
        const cellRef = XLSX.utils.encode_cell({ r, c: amountColIndex });
        const cell = worksheet[cellRef];
        if (cell && cell.t === "n") cell.z = "#,##0.00";
      }

      // ===== AUTOFILTER on the transaction table =====
      // (Freeze-pane is not exposed by this SheetJS build's public API —
      // would require hand-writing sheetView XML, not worth the fragility
      // for this export.)
      worksheet["!autofilter"] = {
        ref: XLSX.utils.encode_range(
          { r: headerRowIndex, c: 0 },
          { r: headerRowIndex + this.transactions.length, c: COLS.length - 1 },
        ),
      };

      // ===== MERGE TITLE CELLS ACROSS THE TABLE WIDTH =====
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: COLS.length - 1 } },
      ];

      // ===== CREATE WORKBOOK =====
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      // ===== DOWNLOAD =====
      XLSX.writeFile(workbook, `transactions-report-${this.periodSuffix()}.xlsx`);
    },

    // Bare period value shared by the PDF header line, the Excel period
    // block/filename, and the zip filename — each caller adds its own
    // label/prefix around this. "range" has no single bare value (it's
    // inherently a from/to pair) so callers handle that case themselves.
    periodSuffix() {
      if (this.filters.type === "month") return this.formattedMonth;
      if (this.filters.type === "year") return String(this.filters.year);
      return `${this.formattedFrom}-to-${this.formattedTo}`;
    },
  },
};
</script>

<style scoped>
.rpt-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.rpt-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.rpt-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rpt-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.rpt-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

/* ================= FILTER BAR ================= */
.rpt-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.rpt-actions {
  display: flex;
  align-items: center;
  padding-top: 4px;
}

.rpt-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

/* ================= KPI CARDS ================= */
.rpt-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.rpt-kpi-card--income {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.rpt-kpi-card--expense {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.rpt-kpi-card--profit {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}

.rpt-kpi-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.rpt-kpi-value {
  font-size: 24px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
